import React from 'react'
import { Check, Lock } from 'lucide-react'
import type { OverviewMilestoneState } from '@kiao-voice/core'
import { cn } from './cn'

/**
 * 成就（設計側 css/kiao-mobile-sleek.css 的 `.s-ring`、`.s-arow`）：成就感來自圓環畫滿的那一刻，
 * 不靠尺寸或色塊。拿到＝完成色滿圈；進行中＝淡圈上一段墨色弧；付費才有＝只有淡圈。
 * 狀態沿用 overview API 的 `OverviewMilestoneState`，不另列一份。
 */

const RADIUS = 26
const CIRCUMFERENCE = 2 * Math.PI * RADIUS

export type AchievementRingSize = 'md' | 'lg'

export interface AchievementRingProps {
  status: OverviewMilestoneState
  /** 0…1，進行中才用；0 只畫淡圈。 */
  fraction?: number
  icon: React.ReactNode
  /** 剛拿到、還沒看過：右上一顆紅點（文字的「新」由列負責念）。 */
  isNew?: boolean
  /** md 44（列）；lg 56（獎章格）。 */
  size?: AchievementRingSize
}

export function AchievementRing({
  status,
  fraction = 0,
  icon,
  isNew = false,
  size = 'md',
}: AchievementRingProps) {
  const progress = Math.min(1, Math.max(0, Number.isFinite(fraction) ? fraction : 0))
  const ring = 'fill-none [stroke-width:1.5]'
  return (
    <span
      className={cn(
        'relative grid shrink-0 place-items-center',
        size === 'lg' ? 'size-14' : 'size-11'
      )}
    >
      <svg aria-hidden="true" viewBox="0 0 56 56" className="absolute inset-0 size-full -rotate-90">
        {status === 'earned' ? (
          <circle cx="28" cy="28" r={RADIUS} className={cn(ring, 'stroke-[var(--ok)]')} />
        ) : (
          <>
            <circle cx="28" cy="28" r={RADIUS} className={cn(ring, 'stroke-[var(--line)]')} />
            {status === 'progress' && progress > 0 ? (
              <circle
                cx="28"
                cy="28"
                r={RADIUS}
                className={cn(ring, 'stroke-foreground')}
                strokeDasharray={CIRCUMFERENCE}
                strokeDashoffset={CIRCUMFERENCE * (1 - progress)}
              />
            ) : null}
          </>
        )}
      </svg>
      <span
        className={cn(
          'relative flex items-center justify-center [&>svg]:size-full [&>svg]:[stroke-width:1.5]',
          size === 'lg' ? 'size-5' : 'size-4.5',
          status === 'earned' ? 'text-ok' : 'text-muted'
        )}
      >
        {icon}
      </span>
      {isNew ? (
        <span
          data-new-dot=""
          aria-hidden="true"
          className={cn(
            'absolute size-2 rounded-full bg-brand shadow-[0_0_0_2px_var(--bg)]',
            size === 'lg' ? 'right-0.75 top-0.75' : 'right-0.5 top-0.5'
          )}
        />
      ) : null}
    </span>
  )
}

export interface AchievementRowProps {
  status: OverviewMilestoneState
  fraction?: number
  icon: React.ReactNode
  name: React.ReactNode
  /** 名稱下一行：進度（「82,340 / 100,000」）或「升級後解鎖」；拿到的沒有。 */
  meta?: React.ReactNode
  /** 進行中右側的百分比；不給就不寫（新用戶的 0%）。 */
  percent?: React.ReactNode
  isNew?: boolean
  /** 拿到的綠勾的可及名稱。 */
  earnedLabel: string
  /** 付費才有的鎖的可及名稱。 */
  lockedLabel: string
  /** 剛拿到時綠勾前的紅字。 */
  newLabel: string
}

/** 一列一枚成就：圓環、名稱與進度、右側綠勾（＋「新」）／百分比／鎖。靜態，不可點。 */
export function AchievementRow({
  status,
  fraction,
  icon,
  name,
  meta,
  percent,
  isNew = false,
  earnedLabel,
  lockedLabel,
  newLabel,
}: AchievementRowProps) {
  return (
    <li className="mx-[var(--m-gutter)] grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3.5 border-t border-[var(--line)] py-3">
      <AchievementRing
        status={status}
        fraction={fraction}
        icon={icon}
        isNew={status === 'earned' && isNew}
      />
      <span className="min-w-0">
        <span className="block text-md font-medium leading-[1.35]">{name}</span>
        {meta != null ? (
          <span className="mt-0.5 block text-xs tabular-nums text-muted">{meta}</span>
        ) : null}
      </span>
      <span className="inline-flex items-center gap-1.5 text-xs tabular-nums text-muted">
        {status === 'earned' ? (
          <>
            {isNew ? <span className="font-semibold text-brand">{newLabel}</span> : null}
            <Check
              role="img"
              aria-label={earnedLabel}
              className="size-4 text-ok [stroke-width:2]"
            />
          </>
        ) : status === 'locked' ? (
          <Lock
            role="img"
            aria-label={lockedLabel}
            className="size-4 text-muted [stroke-width:1.75]"
          />
        ) : (
          percent
        )}
      </span>
    </li>
  )
}

export interface AchievementListProps {
  children: React.ReactNode
  /** 列排成兩欄（寬的分組）。 */
  columns?: 1 | 2
  className?: string
}

export function AchievementList({ children, columns = 1, className }: AchievementListProps) {
  return (
    <ul className={cn('m-0 list-none p-0', columns === 2 && 'grid grid-cols-2 gap-x-2', className)}>
      {children}
    </ul>
  )
}
